import React, { createRef, useState } from 'react';
import PropTypes from 'prop-types';
import { motion, AnimatePresence } from 'framer-motion';

import { IconChevronSmall, IconArrowUp } from '$partials/icons/index.js';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const AnchorNavigation = ({ anchors }) => {
    const [showScrollTop, setShowScrollTop] = useState(false);
    const containerRef = createRef();

    const handleAnchorClick = () => {
        // Show the scroll-to-top button when an anchor is clicked on desktop
        if (window.innerWidth >= 768) {
            setShowScrollTop(true);
        }
    };

    const scrollToTop = () => {
        setShowScrollTop(false);
        if (containerRef.current) {
            const elementPosition = containerRef.current.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - 70;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }
    };

    return (
        <div
            className='layout-container'
            ref={containerRef}>
            {/* Scroll to top button - Desktop only */}
            <AnimatePresence>
                {showScrollTop && (
                    <motion.div
                        animate={{ y: 0, opacity: 1 }}
                        className='hidden md:flex fixed bottom-20 left-0 w-full pointer-events-none'
                        exit={{ y: 100, opacity: 0 }}
                        initial={{ y: 100, opacity: 0 }}
                        transition={{ duration: 0.3, ease: 'easeOut' }}>
                        <div className='layout-container w-full flex justify-end pointer-events-none'>
                            <button
                                aria-label='Scroll to top'
                                className='w-50 h-50 rounded-button flex items-center justify-center z-50 pointer-events-auto mx-10'
                                onClick={scrollToTop}
                                style={{ backgroundColor: '#FC5200' }}
                                type='button'>
                                <IconArrowUp colour='white' />
                            </button>
                            <div className='w-1/12 px-10' />
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Desktop  */}
            <div className='hidden md:block px-10 overflow-auto w-4/12 pr-30 pointer-events-auto'>
                <div className='bg-white py-5 pr-5 rounded-[5px]'>
                    <div className='anchor-navigation bg-grey rounded-[5px] flex gap-x-15 pt-25 pb-25 pl-25 pr-30 items-start'>
                        {/* Animated container */}
                        <div
                            className='flex flex-col gap-y-25 overflow-hidden'>
                            {anchors.map((anchor, idx) => {
                                const originalIndex = anchors.findIndex((a) => a.slug === anchor.slug);
                                return (
                                    <a
                                        className='type-style-anchor text-dark-grey flex items-center'
                                        href={`#${anchor.slug}`}
                                        key={anchor.slug}
                                        onClick={handleAnchorClick}>
                                        <span>
                                            {String(originalIndex + 1).padStart(2, '0')}. {anchor.title} <IconChevronSmall className='ml-5 inline-block' />
                                        </span>
                                    </a>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>

            {/* Mobile */}
            <div className='block md:hidden px-10 overflow-auto w-full'>
                <div className='bg-white rounded-[5px]'>
                    <div className='anchor-navigation bg-grey rounded-[5px] flex gap-x-15 py-18 px-14 items-start pointer-events-auto'>
                        <div
                            className='flex flex-col gap-y-15 w-full'>
                            {anchors.map((anchor, idx) => {
                                const originalIndex = anchors.findIndex((a) => a.slug === anchor.slug);
                                return (
                                    <a
                                        className='type-style-anchor text-black'
                                        href={`#${anchor.slug}`}
                                        key={anchor.slug}>
                                        <span className='items-center'>
                                            {String(originalIndex + 1).padStart(2, '0')}. {anchor.title}
                                            <IconChevronSmall className='ml-5 inline-block' />
                                        </span>
                                    </a>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

AnchorNavigation.propTypes = {
    anchors: PropTypes.array,
};

export default AnchorNavigation;
